import React, { useState } from 'react';
import { signInWithPopup, signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, googleProvider } from '../lib/firebase';
import { User } from '../types';
import { isAndroid } from '../utils/platformUtils';
import logo from '../assets/logo.jpg';

interface AuthProps {
  onLogin: (user: User) => void;
  onShowTerms: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onLogin, onShowTerms }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const communityEnabled = !isAndroid();

  const handleAuthError = (err: any) => {
    console.error('Auth Error Full Object:', err);

    if (err.code === 'auth/invalid-api-key' || err.message?.includes('api-key')) {
      setError('Configuration Error: The API Key is invalid.');
    } else if (err.code === 'auth/popup-closed-by-user') {
      setError('Sign-in cancelled.');
    } else if (err.code === 'auth/unauthorized-domain') {
      setError('Domain Unauthorized: You must whitelist this domain in Firebase.');
    } else if (err.code === 'auth/operation-not-supported-in-this-environment') {
      setError('Popup sign-in is not supported in this environment.');
    } else {
      setError(`Sign-in Failed: ${err.message}`);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle({
          useCredentialManager: false
        });
        const idToken = result.credential?.idToken;
        const accessToken = result.credential?.accessToken;
        if (!idToken && !accessToken) {
          throw new Error('Google Sign-In did not return an ID token or access token.');
        }
        const credential = GoogleAuthProvider.credential(idToken, accessToken);
        const firebaseResult = await signInWithCredential(auth, credential);

        const loggedInUser: User = {
          uid: firebaseResult.user.uid,
          displayName: firebaseResult.user.displayName || result.user?.displayName || 'User',
          email: firebaseResult.user.email || result.user?.email || '',
          photoURL: firebaseResult.user.photoURL || result.user?.photoUrl || ''
        };
        onLogin(loggedInUser);
        return;
      }

      const result = await signInWithPopup(auth, googleProvider);
      const loggedInUser: User = {
        uid: result.user.uid,
        displayName: result.user.displayName || 'User',
        email: result.user.email || '',
        photoURL: result.user.photoURL || ''
      };
      onLogin(loggedInUser);
    } catch (err: any) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 bg-brand-950">
      <div className="text-center mb-12">
        <img src={logo} alt="Aficionado Logo" className="w-32 h-32 mx-auto mb-6 object-contain drop-shadow-2xl rounded-full border-4 border-white" />
        <h1 className="text-5xl font-serif text-brand-50 mb-4 tracking-tight">Aficionado</h1>
        <p className="text-brand-300 text-lg max-w-md mx-auto">
          {communityEnabled
            ? 'Discover lounges, track your humidors content, and join a community of refined tastes.'
            : 'Discover lounges and track your humidor content with a premium cigar journal experience.'}
        </p>
      </div>

      <div className="w-full max-w-sm bg-brand-900 p-8 rounded-xl border border-brand-800 shadow-2xl">
        <h3 className="text-xl text-brand-100 font-medium mb-6 text-center">Sign in to continue</h3>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg break-words font-mono">
            {error}
          </div>
        )}

        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 bg-brand-900 hover:bg-brand-800 text-brand-100 font-medium py-3 px-4 rounded-lg transition-colors shadow-md border border-brand-700 relative overflow-hidden group"
        >
          {loading && (
            <div className="absolute inset-0 bg-brand-900/80 flex items-center justify-center z-10">
              <div className="animate-spin w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full"></div>
            </div>
          )}
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.04-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span>Sign in with Google</span>
        </button>

        <p className="text-center text-brand-500 text-xs mt-4 relative z-10">
          By continuing, you agree to our{' '}
          <button
            type="button"
            onClick={onShowTerms}
            className="underline hover:text-gold-500 transition-colors cursor-pointer"
          >
            Terms of Service
          </button>
          {' '}and Privacy Policy.
        </p>
      </div>
    </div>
  );
};
